import { type Node, variablesOf } from "@/lib/tool-kit/calculus/ast";
import { compile } from "@/lib/tool-kit/calculus/evaluate";
import { parseExpression } from "@/lib/tool-kit/calculus/parse";

const DIGIT = /\d/;

/**
 * Rewrite a decimal comma as a dot: "1,5" -> "1.5", "-0,25" -> "-0.25", "pi/2+0,5" ->
 * "pi/2+0.5". Applies only when the text has exactly one comma, no dot, and that comma
 * sits between two digits outside any parentheses — so "1,5,2" and a two-argument call
 * like "log(8,2)" are left alone for the parser to judge. Everything else is unchanged.
 */
export function normalizeDecimalComma(text: string): string {
  const first = text.indexOf(",");
  if (first <= 0 || first !== text.lastIndexOf(",") || text.includes(".")) {
    return text;
  }
  if (
    !(DIGIT.test(text[first - 1] ?? "") && DIGIT.test(text[first + 1] ?? ""))
  ) {
    return text;
  }
  let depth = 0;
  for (const char of text.slice(0, first)) {
    if (char === "(") {
      depth += 1;
    } else if (char === ")") {
      depth -= 1;
    }
  }
  return depth === 0
    ? `${text.slice(0, first)}.${text.slice(first + 1)}`
    : text;
}

export interface Constant {
  ast: Node;
  value: number;
}

/**
 * A constant typed by the reader — 2, −1.5, pi/4, 1/3, sqrt(2) — as a tree and a number.
 * Null when the field is blank; "invalid" when it does not parse, contains a variable, or
 * has no real value (1/0).
 */
export function readConstant(text: string): Constant | "invalid" | null {
  if (text.trim() === "") {
    return null;
  }
  const parsed = parseExpression(normalizeDecimalComma(text));
  if (!parsed.ok || variablesOf(parsed.ast).length > 0) {
    return "invalid";
  }
  const value = compile(parsed.ast)(0);
  return Number.isFinite(value) ? { ast: parsed.ast, value } : "invalid";
}

export interface Limit {
  value: number;
  /** The expression for a finite limit; null for ±∞. */
  ast: Node | null;
}

const POSITIVE_INFINITY = /^\+?\s*(?:inf|infinity|∞)$/i;
const NEGATIVE_INFINITY = /^[-−–]\s*(?:inf|infinity|∞)$/i;

/** An integration limit: a constant, or inf / infinity / ∞ with an optional sign. */
export function readLimit(text: string): Limit | "invalid" | null {
  const trimmed = text.trim();
  if (POSITIVE_INFINITY.test(trimmed)) {
    return { value: Number.POSITIVE_INFINITY, ast: null };
  }
  if (NEGATIVE_INFINITY.test(trimmed)) {
    return { value: Number.NEGATIVE_INFINITY, ast: null };
  }
  const constant = readConstant(text);
  if (constant === null || constant === "invalid") {
    return constant;
  }
  return { value: constant.value, ast: constant.ast };
}
